import { useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Control, useController } from "react-hook-form";

import { AddRegular } from "@fluentui/react-icons";
import {
  Button,
  Field,
  Input,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";

import UniqueList from "@aglocal/web/helpers/UniqueList";
import normalizeSkill from "@aglocal/web/helpers/normalizeSkill";

import Tag, { TagGroup } from "@aglocal/web/components/Tag";
import Stack from "@aglocal/web/components/Stack";

import { UseFormSetDefault } from "@/hooks/useSetDefault";

import { PostMeetingFormWithSummary } from "../form";
import { FIELD_LABELS } from "../messages";

const messages = defineMessages({
  removedSkills: {
    defaultMessage: "Removed Skills",
    description:
      "Label of the list of skills the recruiter removed from the talent's profile on the wrap-up form.",
  },
  additionalSkills: {
    defaultMessage: "Additional Skills",
    description:
      "Label of the input used to add a new skill on the wrap-up form.",
  },
  addSkill: {
    defaultMessage: "Add skill",
    description: "Accessible label of the button that adds the typed skill.",
  },
  removeSkill: {
    defaultMessage: "remove",
    description:
      "Accessible label of the icon that removes a skill from the list.",
  },
  restoreSkill: {
    defaultMessage: "add",
    description:
      "Accessible label of the icon that puts a removed skill back on the list.",
  },
});

const useStyles = makeStyles({
  unclickable: {
    pointerEvents: "none",
  },
  dismissIcon: {
    cursor: "pointer",
    pointerEvents: "auto",
  },
  old: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
  },
  new: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  removed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
  },
});

interface SkillTagProps {
  value: string;
  variant: "old" | "new" | "removed";
  disabled?: boolean;
}

function SkillTag({ value, variant, disabled }: SkillTagProps) {
  const { $t } = useIntl();
  const styles = useStyles();
  return (
    <Tag
      as="span"
      className={mergeClasses(styles.unclickable, styles[variant])}
      value={value}
      dismissIcon={
        disabled
          ? null
          : {
              className: styles.dismissIcon,
              "aria-label": $t(messages.removeSkill),
              ...(variant === "removed" && {
                "aria-label": $t(messages.restoreSkill),
                children: <AddRegular />,
              }),
            }
      }
      dismissible={!disabled}
    >
      {value}
    </Tag>
  );
}

export interface SkillsFieldProps {
  existingSkills?: readonly string[];
  suggestedSkills?: readonly string[];
  negativeSkills?: readonly string[];
  control: Control<PostMeetingFormWithSummary>;
  setDefault: UseFormSetDefault<PostMeetingFormWithSummary>;
}

export default function SkillsField({
  control,
  setDefault,
  ...props
}: SkillsFieldProps) {
  const { $t } = useIntl();
  const {
    field: { onChange, value },
    fieldState: { error },
    formState: { isSubmitting, isSubmitSuccessful },
  } = useController({
    control,
    name: "skills",
  });

  const [input, setInput] = useState("");

  const skills = useMemo(() => new UniqueList(value, normalizeSkill), [value]);

  const existingSkills = useMemo(
    () => new UniqueList(props.existingSkills, normalizeSkill),
    [props.existingSkills],
  );

  const negativeSkills = useMemo(
    () => new UniqueList(props.negativeSkills, normalizeSkill),
    [props.negativeSkills],
  );

  const suggestedSkills = useMemo(
    () => new UniqueList(props.suggestedSkills, normalizeSkill),
    [props.suggestedSkills],
  );

  const defaultSkills = useMemo(
    () => UniqueList.union(existingSkills, suggestedSkills),
    [existingSkills, suggestedSkills],
  );

  const removedSkills = useMemo(
    () => UniqueList.difference(defaultSkills, skills),
    [defaultSkills, skills],
  );

  useEffect(() => {
    setDefault(
      "skills",
      Array.from(UniqueList.difference(defaultSkills, negativeSkills)),
    );
  }, [defaultSkills, setDefault, negativeSkills]);

  const handleRemove = (skill: string) => {
    onChange(value.filter((s) => s !== skill));
  };

  const handleAdd = (skill: string) => {
    if (skills.contains(skill)) {
      return;
    }
    onChange([...value, skill]);
  };

  const normalizedInput = input.trim().replace(/\s+/g, " ");
  const handleAddNew = () => {
    if (!normalizedInput.length) {
      return;
    }
    handleAdd(normalizedInput);
    setInput("");
  };

  return (
    <Stack gap="m">
      <Field
        label={$t(FIELD_LABELS.skills)}
        validationState={error ? "error" : undefined}
        validationMessage={error?.message}
      >
        <TagGroup
          onDismiss={(event, { value }) => {
            handleRemove(value);
          }}
        >
          {skills.map((skill) => (
            <SkillTag
              key={skill}
              value={skill}
              variant={existingSkills.contains(skill) ? "old" : "new"}
              disabled={isSubmitting || isSubmitSuccessful}
            />
          ))}
        </TagGroup>
      </Field>
      {removedSkills.length > 0 && (
        <Field label={$t(messages.removedSkills)}>
          <TagGroup
            onDismiss={(event, { value }) => {
              handleAdd(value);
            }}
          >
            {removedSkills.map((skill) => (
              <SkillTag
                key={skill}
                value={skill}
                variant="removed"
                disabled={isSubmitting || isSubmitSuccessful}
              />
            ))}
          </TagGroup>
        </Field>
      )}
      <Field label={$t(messages.additionalSkills)}>
        <Input
          value={input}
          disabled={isSubmitting || isSubmitSuccessful}
          onChange={(event) => {
            setInput(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleAddNew();
            }
          }}
          contentAfter={
            <Button
              icon={<AddRegular />}
              aria-label={$t(messages.addSkill)}
              appearance="transparent"
              size="small"
              disabled={
                !normalizedInput.length || isSubmitting || isSubmitSuccessful
              }
              onClick={(event) => {
                event.preventDefault();
                handleAddNew();
              }}
            />
          }
        />
      </Field>
    </Stack>
  );
}
